import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core'
import { FlexLayoutModule } from '@angular/flex-layout'
import { MatChipsModule } from '@angular/material/chips'
import { MatDividerModule } from '@angular/material/divider'
import { MatIconModule } from '@angular/material/icon'
import { EstimateBalance, PAYMENT_PLAN_CADENCE_DAYS, Transaction, TransactionModel } from '@mmx/shared'
import { TranslateModule } from '@ngx-translate/core'

/**
 * Read-only summary of a balance's active payment plan; drives both the console
 * 'has-plan' panel and the patient-portal balance view. No actions — wrap with a
 * footer in the consumer when controls are needed.
 *
 * Conventions encoded here: installment #1 is the upfront PaymentIntent at day 0;
 * installments 2..N fire at `startedAt + (k-1) * cadence`. Derivations live with
 * their respective computeds. Consumers pass `transactions` to keep the component
 * reusable from contexts that already have them.
 */
@Component({
  selector: 'mmx-balance-payment-plan-summary',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FlexLayoutModule,
    MatChipsModule,
    MatDividerModule,
    MatIconModule,
    TranslateModule,
  ],
  templateUrl: './balance-payment-plan-summary.component.html',
  styleUrls: ['./balance-payment-plan-summary.component.scss'],
})
export class BalancePaymentPlanSummaryComponent {
  readonly balance = input<EstimateBalance | null>(null)
  readonly transactions = input<TransactionModel[] | null>(null)

  readonly plan = computed(() => this.balance()?.paymentPlan ?? null)

  /** Falls back to `monthlyAmount` for plans persisted before `firstPayment` was added. */
  readonly firstPayment = computed<number>(() => {
    const plan = this.plan()
    if (!plan) {
      return 0
    }
    return plan.firstPayment ?? plan.monthlyAmount
  })

  /** True when month 1 differs from months 2..N (clinic minimum upfront, operator override, etc.). */
  readonly hasDistinctFirstPayment = computed<boolean>(() => {
    const plan = this.plan()
    if (!plan) {
      return false
    }
    return plan.firstPayment != null && plan.firstPayment !== plan.monthlyAmount
  })

  readonly installmentsPaid = computed<number>(() => {
    const plan = this.plan()
    const balance = this.balance()
    if (!plan || !balance) {
      return 0
    }
    const monthlyAmount = plan.monthlyAmount
    if (!(monthlyAmount > 0)) {
      return 0
    }
    const paid = balance.paidAmount ?? 0
    const upfront = this.firstPayment()
    // Upfront installment hasn't fully cleared yet — show 0 rather than
    // claiming partial credit toward installment #1.
    if (paid < upfront) {
      return 0
    }
    const additional = Math.floor((paid - upfront) / monthlyAmount)
    const total = 1 + additional
    // Cap at total months — a clinic-side over-collection could push paidAmount
    // past total without invalidating the plan; we don't want "Paid 7 of 6" in the UI.
    return Math.max(0, Math.min(plan.months, total))
  })

  readonly installmentsRemaining = computed<number>(() => {
    const plan = this.plan()
    if (!plan) {
      return 0
    }
    return Math.max(0, plan.months - this.installmentsPaid())
  })

  // Clamped to [0, 100] — over-collection mustn't render >100%; zero-month plan can't divide by zero.
  readonly progressPercent = computed<number>(() => {
    const plan = this.plan()
    if (!plan || plan.months <= 0) {
      return 0
    }
    return Math.max(0, Math.min(100, Math.round((this.installmentsPaid() / plan.months) * 100)))
  })

  readonly nextChargeAt = computed<Date | null>(() => {
    const plan = this.plan()
    if (!plan || this.installmentsRemaining() === 0) {
      return null
    }
    // installment #1 is the upfront PaymentIntent at day 0; the k-th installment
    // (k >= 2) fires at startedAt + (k-1) * cadence. So after `installmentsPaid`
    // charges have happened, the next one is at startedAt + installmentsPaid * cadence.
    return this.addDays(this.startedAt(plan), this.installmentsPaid() * PAYMENT_PLAN_CADENCE_DAYS)
  })

  readonly endsAt = computed<Date | null>(() => {
    const plan = this.plan()
    if (!plan) {
      return null
    }
    // Last charge of a `months`-installment plan fires (months - 1) cadence steps
    // after the upfront. Clamp at 0 to keep a 1-month plan rendering startedAt.
    const lastOffset = Math.max(0, plan.months - 1)
    return this.addDays(this.startedAt(plan), lastOffset * PAYMENT_PLAN_CADENCE_DAYS)
  })

  readonly failedCharge = computed<TransactionModel | null>(() => {
    const txs = this.transactions()
    if (!txs?.length) {
      return null
    }
    // Latest FAILED PAYMENT, regardless of provider — the banner is about
    // surfacing a Stripe charge attempt the patient should know about.
    const failed = txs
      .filter((t) => t.status === Transaction.STATUS.FAILED && t.type === Transaction.TYPE.PAYMENT)
      .sort((a, b) => this.timestamp(b.createdAt) - this.timestamp(a.createdAt))
    return failed[0] ?? null
  })

  private startedAt(plan: { startedAt: Date | string }): Date {
    return plan.startedAt instanceof Date ? plan.startedAt : new Date(plan.startedAt)
  }

  private addDays(date: Date, days: number): Date {
    const next = new Date(date.getTime())
    next.setUTCDate(next.getUTCDate() + days)
    return next
  }

  // TransactionModel.createdAt may be a moment.Moment (per BaseModel.transformDate),
  // a Date, or a string. Coerce defensively rather than assume a single shape.
  private timestamp(value: unknown): number {
    if (!value) {
      return 0
    }
    if (value instanceof Date) {
      return value.getTime()
    }
    const maybeMoment = value as { valueOf?: () => number, toString?: () => string }
    if (typeof maybeMoment.valueOf === 'function') {
      const n = maybeMoment.valueOf()
      if (typeof n === 'number' && !Number.isNaN(n)) {
        return n
      }
    }
    const parsed = new Date(String(value))
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime()
  }
}
